import { OrdersApi, SellerOrderTrackingDto } from '@/app/services/orders.api';
import { CommonModule } from '@angular/common';
import { Component, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ShamCustomerService01Icon,
  ShamTick01Icon,
  ShamPackageDeliveredIcon,
  ShamArrowLineLeftIcon,
  ShamTruckIcon,
  ShamLocationAddressIcon,
  ShamPackageIcon,
  ShamPaymentSuccess01Icon,
  ShamTicket01BlueIcon,
} from 'sham-components/icons';
import { MEDIA_BASE_URL } from '@/environments/environment';
import { ReasonOption, Ticket, TicketsApi } from '@/app/services/tickets.api';

export interface OrderStep {
  label: string;
  date: string;
  status: 'completed' | 'current' | 'upcoming';
  icon: string;
}

@Component({
  selector: 'app-order-tracking-page',
  standalone: true,
  imports: [
    CommonModule,
    ShamTruckIcon,
    ShamLocationAddressIcon,
    ShamArrowLineLeftIcon,
    ShamPackageIcon,
    ShamTick01Icon,
    ShamPackageDeliveredIcon,
    ShamPaymentSuccess01Icon,
    TranslateModule,
    RouterLink,
    ShamCustomerService01Icon,
    ShamTicket01BlueIcon,
  ],
  templateUrl: './order-tracking-page.html',
  styleUrl: './order-tracking-page.css',
})
export class OrderTrackingPage {
  private route = inject(ActivatedRoute);
  private readonly ordersApi = inject(OrdersApi);
  private readonly ticketsApi = inject(TicketsApi);
  private translate = inject(TranslateService);
  orderId = signal<string | null>(null);
  trackingData = signal<SellerOrderTrackingDto | null>(null);
  isLoading = signal<boolean>(true);
  isCreateModalOpen = signal(false);
  isSuccessModalOpen = signal(false);
  isCreating = signal(false);
  createError = signal<string | null>(null);
  createdTicket = signal<Ticket | null>(null);
  reasonOptions = signal<Array<{ id: string; name: string }>>([]);
  ticketForm = signal({
    reason: '',
    customReason: '',
    description: '',
  });

  isCancelled = computed(() => {
    const data = this.trackingData();
    return data?.statusInfo?.status === 'Cancelled';
  });
  private router = inject(Router);

  goToSellerProfile(sellerId: string) {
    if (!sellerId) return;
    this.router.navigate(['/seller', sellerId]);
  }

  steps = computed(() => {
    const data = this.trackingData();
    if (!data) return [];

    const t = data.timeline;

    const formatDate = (d: string | null) =>
      d
        ? new Date(d).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : this.translate.instant('pending');

    return [
      {
        label: 'created',
        date: formatDate(data.createdAtUtc),
        icon: 'checkmark',
        completed: !!data.createdAtUtc,
      },
      {
        label: 'proceeding',
        date: formatDate(t.confirmedAtUtc),
        icon: 'invoice',
        completed: !!t.confirmedAtUtc,
      },
      {
        label: 'on_the_way',
        date: formatDate(t.shippedAtUtc),
        icon: 'truck',
        completed: !!t.shippedAtUtc,
      },
      {
        label: 'delivered',
        date: formatDate(t.deliveredAtUtc),
        icon: 'package',
        completed: !!t.deliveredAtUtc,
      },
    ];
  });

  getOptionsText(options: any[]): string {
    if (!options || options.length === 0) return '';

    return options
      .map((opt) => `${opt.attributeName}: ${opt.attributeValueName}`)
      .join(', ');
  }
  get progress(): number {
    const data = this.trackingData();
    if (!data) return 0;

    const t = data.timeline;

    const steps = [
      !!data.createdAtUtc,
      !!t.confirmedAtUtc,
      !!t.shippedAtUtc,
      !!t.deliveredAtUtc,
    ];

    const completed = steps.filter(Boolean).length;

    if (completed === 0) return 0;

    return ((completed - 1) / 3) * 100;
  }

  ngOnInit() {
    this.loadTicketReasons();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.orderId.set(id);
      this.fetchTrackingData(id);
    }
  }

  fetchTrackingData(sellerOrderId: string) {
    this.isLoading.set(true);
    this.ordersApi.getSellerOrderTracking(sellerOrderId).subscribe({
      next: (res) => {
        this.trackingData.set(res);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error fetching tracking data', err);
        this.isLoading.set(false);
      },
    });
  }

  goBack() {
    this.router.navigate(['/home']);
  }

  openCreateModal() {
    this.ticketForm.set({
      reason: '',
      customReason: '',
      description: '',
    });
    this.createError.set(null);
    this.isCreateModalOpen.set(true);
  }

  closeCreateModal() {
    if (this.isCreating()) {
      return;
    }

    this.isCreateModalOpen.set(false);
    this.createError.set(null);
  }

  updateTicketForm(field: 'reason' | 'customReason' | 'description', value: string) {
    this.ticketForm.update((form) => ({
      ...form,
      [field]: value,
      ...(field === 'reason' && value !== 'Other' ? { customReason: '' } : {}),
    }));
  }

  createTicket() {
    const data = this.trackingData();
    const form = this.ticketForm();
    const description = form.description.trim();
    const customReason = form.customReason.trim();

    if (!data) {
      this.createError.set('Seller order data is not available.');
      return;
    }

    if (!form.reason || !description) {
      this.createError.set('Please fill all required fields.');
      return;
    }

    if (form.reason === 'Other' && !customReason) {
      this.createError.set('Custom reason is required.');
      return;
    }

    this.isCreating.set(true);
    this.createError.set(null);

    this.ticketsApi
      .createCustomerTicket({
        sellerOrderId: data.sellerOrderId,
        reason: form.reason,
        customReason: form.reason === 'Other' ? customReason : null,
        description,
      })
      .subscribe({
        next: (ticket) => {
          this.createdTicket.set(ticket);
          this.isCreateModalOpen.set(false);
          this.isSuccessModalOpen.set(true);
          this.isCreating.set(false);
        },
        error: (err) => {
          console.error('Error creating ticket:', err);
          this.createError.set('Could not create ticket. Please try again.');
          this.isCreating.set(false);
        },
      });
  }

  closeSuccessModal() {
    this.isSuccessModalOpen.set(false);
    this.createdTicket.set(null);
  }

  getImageUrl(path?: string | null): string {
    if (!path) return '';
    return path.startsWith('http') ? path : `${MEDIA_BASE_URL}${path}`;
  }
  goToBack() {
    history.back();
  }

  private loadTicketReasons() {
    this.ticketsApi.getTicketReasons().subscribe({
      next: (reasons) =>
        this.reasonOptions.set(reasons.map((reason) => this.mapReasonOption(reason))),
      error: () =>
        this.reasonOptions.set([
          { id: 'DeliveryProblem', name: 'Delivery problem' },
          { id: 'WrongItem', name: 'Wrong item' },
          { id: 'RefundRequest', name: 'Refund request' },
          { id: 'Other', name: 'Other' },
        ]),
    });
  }

  private mapReasonOption(reason: ReasonOption) {
    return {
      id: reason.value,
      name: reason.displayName,
    };
  }
}
